import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "@8x8/oxygen-button";
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter,
} from "@8x8/oxygen-modal";
import { notify, TYPE } from "@8x8/oxygen-toaster";
import SearchIcon from "@8x8/oxygen-icon/novo/SearchIcon";

import { searchCustomer, applyReactCustomer } from "@/react/features/customer";
import {
  closeCustomerSelectModal,
  selectIsCustomerModalVisible,
  selectReactCustomer,
} from "@/react/features/customer/customerSlice";

import useManagedPbxSelection from "./pbx/useManagedPbxSelection";
import useManagedTenantSelection from "./tenant/useManagedTenantSelection";
import { Content, CustomWrapper } from "./styled";
import { CurrentCustomerInfo } from "./current-customer-info";
import { SearchControls } from "./search-controls";
import { SearchResults } from "./results";
import SearchStatus from "./searchStatus";
import SectionTitle from "./SectionTitle";

const isValid = (payload) => {
  if (!payload || !payload?.field || !payload?.term) {
    console.debug(
      "[CustomerSelectModal] Invalid payload.",
      payload ? JSON.stringify(payload) : payload
    );

    return false;
  }

  return true;
};

const CustomerSelectModal = () => {
  const dispatch = useDispatch();

  const isVisible = useSelector(selectIsCustomerModalVisible);

  // The active customer is the customer that is already selected at the moment
  // of mounting the modal, if any.
  const activeCustomer = useSelector(selectReactCustomer);

  // We have a sort of "stage" area for the customer - the modifications
  // done in this modal (PBX pre-selections and search result selection)
  // will not be instantly commited to state, but instead saved/"staged" in
  // the modal's state until the user clicks "Confirm"
  const [stagedCustomer, setStagedCustomer] = React.useState(null);
  React.useEffect(() => {
    setStagedCustomer(activeCustomer);
  }, [activeCustomer]);

  const [pbxOptions, selectedPbx] = useManagedPbxSelection(stagedCustomer);
  const [tenantOptions, selectedTenant] =
    useManagedTenantSelection(stagedCustomer);

  const [portalTarget, setPortalTarget] = React.useState(null);
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchStatus, setSearchStatus] = React.useState(SearchStatus.INITIAL);

  const onSearch = async (payload) => {
    if (!isValid(payload)) {
      return;
    }

    try {
      setSearchStatus(SearchStatus.LOADING);
      const response = await searchCustomer(payload.field, payload.term);
      const data = response?.data ?? [];
      setSearchResults(data);
      setSearchStatus(
        data.length ? SearchStatus.SHOW_RESULTS : SearchStatus.NO_RESULTS
      );
    } catch (error) {
      console.error("[CustomerSelectModal.onSearch] Search failed.", error);
      notify({
        content: `Customer search failed. ${error?.message}`,
        type: TYPE.error,
        duration: 3000,
        hideCloseControl: true,
      });

      setSearchStatus(SearchStatus.NO_RESULTS);
    }
  };

  const onClearCustomer = React.useCallback(() => {
    notify({
      content:
        "Customer cleared. " +
        "Click 'Confirm' to apply the changes, or simply search for another " +
        "customer.",
      type: TYPE.info,
      duration: 3000,
      hideCloseControl: true,
    });
    setStagedCustomer(null);
  }, [setStagedCustomer]);

  const confirmSelectedCustomer = React.useCallback(() => {
    dispatch(applyReactCustomer(stagedCustomer)).then(() =>
      dispatch(closeCustomerSelectModal())
    );
  }, [dispatch, stagedCustomer]);
  const onClose = React.useCallback(() => {
    // Reset the staged customer to the Redux customer, just in case
    // that the user has pressed Clear, but did not apply the changes.
    setStagedCustomer(activeCustomer);
    dispatch(closeCustomerSelectModal());
  }, [setStagedCustomer, activeCustomer, dispatch, closeCustomerSelectModal]);

  const onSearchResultSelected = React.useCallback(
    (data) => setStagedCustomer(data),
    [setStagedCustomer]
  );

  const onPbxUpdated = React.useCallback(
    (selectedPbx) => {
      setStagedCustomer({
        ...stagedCustomer,
        preselectedPbxId: selectedPbx.value,
      });
    },
    [stagedCustomer, setStagedCustomer]
  );
  const onTenantUpdated = React.useCallback(
    (selectedTenant) => {
      setStagedCustomer({
        ...stagedCustomer,
        preselectedTenantId: selectedTenant.value,
      });
    },
    [stagedCustomer, setStagedCustomer]
  );

  return (
    <Modal
      isVisible={isVisible}
      portalRef={(e) => setPortalTarget(e)}
      size={"big"}
      onClose={onClose}
      shouldCloseOnEsc={true}
      allowOutsideClick={true}
      shouldCloseOnOverlayClick={true}
    >
      <CustomWrapper>
        <ModalHeader title="Select Customer">
          Select the customer you want to work on. To apply any change,{" "}
          <b>including clearing the customer</b>, press the <b>Confirm</b>{" "}
          button. When searching, pick one search result using the{" "}
          <b>checkmark</b> button on the right-hand side of the result.
        </ModalHeader>
        <ModalContent>
          <Content>
            <CurrentCustomerInfo
              /* PBX */
              selectedPbx={selectedPbx}
              pbxOptions={pbxOptions}
              onPbxUpdated={onPbxUpdated}
              /* Tenant */
              selectedTenant={selectedTenant}
              tenantOptions={tenantOptions}
              onTenantUpdated={onTenantUpdated}
              /* General */
              customerData={stagedCustomer}
              onClearCustomer={onClearCustomer}
              menuPortalTarget={portalTarget}
            />
            <SectionTitle renderIcon={() => <SearchIcon />}>
              Search
            </SectionTitle>
            <SearchControls
              onSearch={onSearch}
              menuPortalTarget={portalTarget}
            />
            <SearchResults
              searchStatus={searchStatus}
              searchResults={searchResults}
              onCustomerSelected={onSearchResultSelected}
              menuPortalTarget={portalTarget}
            />
          </Content>
        </ModalContent>
        <ModalFooter>
          <Button onClick={confirmSelectedCustomer}>Confirm</Button>
          <Button secondary onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </CustomWrapper>
    </Modal>
  );
};

export default CustomerSelectModal;
